import React from 'react';
import { Card, Typography, Steps, Table, Alert, Divider, Tag } from 'antd';
import PageHeader from '../../components/PageHeader';

const { Title, Paragraph, Text } = Typography;

// KAN-167: the reference page linked from the provisioning and top-up modals
// and from the eSIM bundle card. Written for the person who creates eSIMs for
// customers, not for developers.
// KAN-167: the reference for the person who creates eSIMs for customers, not
// for developers. Rendered as a page (/admin/esim-guide) and inside a drawer from
// the provisioning / top-up modals and the bundle card (EsimGuideLink), so it
// never leaves the flow the operator is in.
export const EsimGuideContent: React.FC = () => (
  <>
    <Card style={{ marginBottom: 24 }}>
      <Title level={4}>What a Travel eSIM is</Title>
      <Paragraph>
        A Travel eSIM is a downloadable Vodafone profile from MIMO's prepaid Travel pool. The end
        user scans a QR code on their phone or router, the profile installs, and the device can use
        data wherever the eSIM's <Text strong>coverage</Text> allows, until its{' '}
        <Text strong>bundle</Text> is used up or expires. There is no physical card and nothing to
        ship.
      </Paragraph>
      <Paragraph>
        Three things define what the customer gets: the <Text strong>bundle</Text> (how much data,
        for how long, and what happens when it runs out), the <Text strong>coverage</Text> (in which
        countries it works) and the <Text strong>QR code</Text> that installs it.
      </Paragraph>
    </Card>

    <Card style={{ marginBottom: 24 }}>
      <Title level={4}>Bundles</Title>
      <Paragraph>
        Bundles come from Vodafone's live catalogue and the list in the portal refreshes
        automatically. Each bundle has a data allowance (200 MB up to 2 TB), a validity (1 day up to
        a year) and a <Text strong>balance type</Text>, which says what happens once the allowance
        is used up:
      </Paragraph>
      <Table
        size="small"
        pagination={false}
        style={{ marginBottom: 16 }}
        dataSource={[
          {
            key: 1,
            type: <Tag color="blue">Standard (hard cap)</Tag>,
            what: 'Data stops when the allowance is used up. Add a top-up or a new bundle to continue. These are the normal bundles and the only ones that can be topped up or stacked.',
          },
          {
            key: 2,
            type: <Tag color="orange">Throttled to 1 Mbps</Tag>,
            what: 'After the allowance the connection keeps working but slows to 1 Mbps, up to a ceiling of 200 GB. Maximum validity 30 days. Cannot be topped up or combined with other bundles.',
          },
          {
            key: 3,
            type: <Tag color="orange">Throttled to 5 Mbps</Tag>,
            what: 'Same as above but slows to 5 Mbps.',
          },
        ]}
        columns={[
          { title: 'Balance type', dataIndex: 'type', width: 200 },
          { title: 'What the customer experiences', dataIndex: 'what' },
        ]}
      />
      <Paragraph>
        <Text strong>Two families of bundles.</Text> <Text strong>Travel SIM</Text> bundles are the
        current catalogue: you choose the data and validity, then choose the coverage separately.{' '}
        <Text strong>Travel Zone</Text> bundles are the older products where the coverage zone is
        part of the bundle (for example "Travel Zone 5 - 50 GB"). Use Travel Zone bundles only when
        a customer specifically needs one.
      </Paragraph>
      <Alert
        type="info"
        showIcon
        message="Validity starts on first use, not when you provision"
        description="A 30-day bundle provisioned today and first used in two weeks expires 30 days after that first use. The eSIM page shows the real expiry once Vodafone has one."
      />
    </Card>

    <Card style={{ marginBottom: 24 }}>
      <Title level={4}>Coverage (roaming profiles)</Title>
      <Paragraph>
        Coverage is a Vodafone <Text strong>roaming control profile</Text>. There are two kinds:
      </Paragraph>
      <ul>
        <li>
          <Text strong>Regional tiers</Text> — one per region (Europe, Global, Africa, Asia, MENA,
          Middle East, North America, Latin America, Caribbean, Oceania), numbered 1 to 5. Tiers are
          cumulative: Europe tier 3 covers every country in Europe zones 1, 2 and 3. A higher tier
          reaches more countries and costs more.
        </li>
        <li>
          <Text strong>Single country</Text> — exactly one country. The cheapest choice when the
          vessel stays in one place.
        </li>
      </ul>
      <Paragraph>
        <Text strong>What "zone" and "tier" mean.</Text> Vodafone sorts the countries of each region
        into price bands called <Text strong>zones</Text>: zone 1 holds the region's core countries
        (cheapest for Vodafone to serve), zone 2 adds the next band, and so on up to zone 5. A{' '}
        <Text strong>tier</Text> is what you buy: tier 1 covers zone 1 only, tier 3 covers zones 1,
        2 and 3. So "Europe – tier 3" simply means "the first three price bands of Europe"; the
        picker tells you how many countries that is and lists them. You never choose a zone
        directly, you choose a tier.
      </Paragraph>
      <Paragraph>
        <Text strong>"Travel Zone 1" to "Travel Zone 10" bundles</Text> are the older fixed
        packages. The number is the coverage zone that package was sold for (1 = the cheapest set of
        countries, 10 = the most expensive). They are bundle and coverage in one, so the coverage
        step is skipped. Vodafone has not published country lists for them; when in doubt, use a
        Travel SIM bundle with an explicit coverage instead.
      </Paragraph>
      <Paragraph>On the eSIM page the covered countries are listed under the bundle.</Paragraph>
    </Card>

    <Card style={{ marginBottom: 24 }}>
      <Title level={4}>Provisioning a new eSIM, step by step</Title>
      <Steps
        direction="vertical"
        size="small"
        current={-1}
        items={[
          {
            title: 'Open Inventory and click "Provision eSIM"',
            description:
              'Reseller admin or super admin only. This consumes a real profile from the Vodafone pool.',
          },
          {
            title: 'Step 1 — Bundle',
            description:
              'Pick the family, then search by size or validity ("5GB", "30D"). The explanation under the list tells you what the customer gets and what happens when the data runs out.',
          },
          {
            title: 'Step 2 — Coverage',
            description:
              'Pick a regional tier or a single country. The countries covered appear underneath. Travel Zone bundles skip this step because their coverage is fixed.',
          },
          {
            title: 'Step 3 — Review and provision',
            description:
              'Check the summary, add an optional reference (vessel or customer name), click Provision.',
          },
          {
            title: 'Wait about a minute',
            description:
              'Vodafone allocates the profile asynchronously. The new eSIM appears in Inventory; sort by "Added" to find it.',
          },
          {
            title: 'Hand over the QR code',
            description:
              'Open the eSIM and click "Show activation QR". The customer scans it on the device. The activation code can be re-fetched at any time; nothing secret is stored in the portal.',
          },
        ]}
      />
    </Card>

    <Card style={{ marginBottom: 24 }}>
      <Title level={4}>Adding data later (top-up)</Title>
      <Paragraph>
        On the eSIM page, the <Text strong>Bundle & remaining data</Text> card has a{' '}
        <Text strong>Top up</Text> button. Pick a bundle and the coverage it should apply to:
      </Paragraph>
      <ul>
        <li>
          <Text strong>Same coverage</Text> as the current bundle: the data is added to the existing
          balance and the expiry becomes the later of the two. Example: 4 GB left with 2 days to go,
          add "10GB-5D" → 14 GB, 5 days.
        </li>
        <li>
          <Text strong>Different coverage</Text>: a second bundle is stacked with its own balance
          and expiry. The eSIM uses whichever bundle matches the country it is in. Vodafone allows
          at most three stacked bundles.
        </li>
        <li>
          <Text strong>Throttled bundles cannot be topped up or stacked.</Text> To move an eSIM to a
          throttled plan, wait until the current bundle expires and provision the throttled bundle
          on its own.
        </li>
      </ul>
    </Card>

    <Card style={{ marginBottom: 24 }}>
      <Title level={4}>Reading the eSIM page</Title>
      <Paragraph>
        <Text strong>Remaining</Text> is read live from Vodafone (they allow one query per minute
        per eSIM, so Refresh may show the last known value for up to a minute).{' '}
        <Text strong>Expires</Text> is the real expiry once the bundle has been used.{' '}
        <Text strong>Used up</Text> means the allowance is gone; <Text strong>Expired</Text> means
        the validity ran out with data left. The <Text strong>Bundle history</Text> table lists
        every bundle and top-up Vodafone has on record for the eSIM (last 90 days) together with the
        portal's own records.
      </Paragraph>
      <Paragraph>
        The <Text strong>Data Usage</Text> chart further down comes from Vodafone's daily usage
        files and lags a day. For a throttled bundle the usage keeps growing after the allowance is
        gone (the customer is still online at reduced speed), so use the bundle card, not the chart,
        to answer "how much is left".
      </Paragraph>
    </Card>

    <Card>
      <Title level={4}>When something goes wrong</Title>
      <ul>
        <li>
          <Text strong>"Unknown bundle id" or "Unknown roaming control profile"</Text> — the
          catalogue changed. Reopen the modal; the lists refresh from Vodafone.
        </li>
        <li>
          <Text strong>"Provisioning failed at the provider"</Text> — usually no eSIM stock left on
          the Travel account. Contact Aspider/Vodafone support before retrying.
        </li>
        <li>
          <Text strong>Top-up rejected</Text> — three bundles already stacked, a throttled bundle on
          the eSIM, or the eSIM is not in Ready, Active or Suspended state.
        </li>
        <li>
          <Text strong>The eSIM does not appear after a few minutes</Text> — check Sync History for
          webhook errors, then the Vodafone portal for the allocation.
        </li>
      </ul>
      <Divider />
      <Text type="secondary">
        Source: Vodafone Travel Mobility service description (May 2026) and Product Bundles
        catalogue (June 2026).
      </Text>
    </Card>
  </>
);

const EsimGuide: React.FC = () => (
  <div className="page-wrapper" style={{ padding: 24, maxWidth: 960 }}>
    <PageHeader title="Travel eSIM guide" />
    <EsimGuideContent />
  </div>
);

export default EsimGuide;
